import {
  TEAM_ANNUAL_DISCOUNT,
  TEAM_BASE_SEAT_PRICE,
  TEAM_MIN_SEATS,
  TEAM_TRIAL_DAYS,
} from '@easy-expense/utils-shared';

import { BillingTerm, IndividualPlanCode } from '../Paddle';

// Team pricing facts are shared with ee-mobile through @easy-expense/utils-shared (EZ-5238) so
// the mobile card and this checkout can never disagree; the names below are kept for the
// existing call sites.
export { TEAM_MIN_SEATS, TEAM_TRIAL_DAYS };

export const MONTHLY_PRICES: Record<'TEAMS_BASIC', number> = {
  TEAMS_BASIC: TEAM_BASE_SEAT_PRICE,
};

// Display-only price for legacy Teams Pro subscribers adding seats.
// Teams Pro is deprecated and cannot be purchased.
export const LEGACY_TEAMS_PRO_MONTHLY_PRICE = 12;

// Seats bundled into the Individual Professional plan; shown on the card and
// not expandable (Team is the plan for adding seats).
export const PROFESSIONAL_SEATS = 3;

// Free-trial lengths advertised on the individual plan cards. These MUST match
// the trial period configured on the corresponding Paddle prices, or the card
// over-promises at checkout. Tracked per plan because Starter and Professional
// bill on separate Paddle prices with independently-configured trials — Starter
// reuses the verified 7-day trial prices (MONTHLY/ANNUAL_7DAYTRIAL_STARTER),
// Professional's trial length must be confirmed against its Paddle price config
// (EZ-4865). The Team trial is TEAM_TRIAL_DAYS above.
export const INDIVIDUAL_TRIAL_DAYS: Record<IndividualPlanCode, number> = {
  STARTER: 7,
  PROFESSIONAL: 7,
};

// Display prices for the individual plans; Paddle checkout shows the
// authoritative amount. Starter is $7.99/month or $74.99/year; Professional
// is $23.99/month or $144.99/year.
export const INDIVIDUAL_MONTHLY_PRICES: Record<IndividualPlanCode, Record<BillingTerm, number>> = {
  STARTER: { monthly: 7.99, annual: 6.25 },
  PROFESSIONAL: { monthly: 23.99, annual: 12.08 },
};

export const INDIVIDUAL_ANNUAL_BILLED_PRICES: Record<IndividualPlanCode, number> = {
  STARTER: 74.99,
  PROFESSIONAL: 144.99,
};

export const ANNUAL_DISCOUNT = TEAM_ANNUAL_DISCOUNT;

export const TEAMS_FAQ_URL =
  'https://help.easy-expense.com/en/help/articles/7184234-team-subscriptions-faq';

export const SETTINGS_BILLING_ROUTE = '/settings?tab=billing';

export function formatPrice(value: number): string {
  return Number.isInteger(value) ? `$${value}` : `$${value.toFixed(2)}`;
}

/**
 * Marks the return from a completed checkout that ran on its own route. Billing settings polls
 * for the new subscription when it sees this, because Paddle's webhook trails the redirect by
 * seconds and a single fetch on mount would show a paying customer the paywall (the failure
 * EZ-5062 fixed for the overlay). Shared by the Team and Individual checkouts, which both land
 * their buyer back on SETTINGS_BILLING_ROUTE.
 */
export const CHECKOUT_COMPLETE_PARAM = 'checkout';
export const CHECKOUT_COMPLETE_VALUE = 'complete';

/** SETTINGS_BILLING_ROUTE with the marker above, i.e. where a completed web checkout returns. */
export const SETTINGS_BILLING_CHECKOUT_COMPLETE_ROUTE = `${SETTINGS_BILLING_ROUTE}&${CHECKOUT_COMPLETE_PARAM}=${CHECKOUT_COMPLETE_VALUE}`;

/** Asks billing settings to open the add-seats picker on arrival (the upgrade modal's Add Seats). */
export const ADD_SEATS_PARAM = 'addSeats';

/** SETTINGS_BILLING_ROUTE with the add-seats marker above. */
export const SETTINGS_BILLING_ADD_SEATS_ROUTE = `${SETTINGS_BILLING_ROUTE}&${ADD_SEATS_PARAM}=1`;
